import { decodeBase64Msgpack } from "./util/msgpack.mjs";
import { getTrendingArtworksOutputSchema } from "./artwork.mjs";
import { sizeConfigResponseSchema } from "./modelSizeConfig.mjs";
import { z } from "zod";
import { oc } from "@orpc/contract";
//#region src/api/orpc/generationModel.ts
const allowedModelTypeLiterals = [
	"SD_V1_MODEL",
	"SDXL_MODEL",
	"DIT7B_MODEL",
	"DIT9_MODEL",
	"MMDIT26A_MODEL",
	"Z_IMAGE_V1_MODEL",
	"CHAT"
];
const generationModelCompatibilityKeyLiterals = [
	"lora",
	"styleMix",
	"controlnet",
	"lowPriority",
	"lightning",
	"shift",
	"rescaleCfg",
	"upscale",
	"enableADetailer",
	"vae",
	"clipSkip",
	"ipAdapter",
	"refImage",
	"negativePrompt",
	"cfgScale",
	"styleKey",
	"watermark",
	"inferenceProfile",
	"samplingMethod",
	"samplingSteps"
];
/**
* The ES feed provider behind `getTrendingArtworksByModel` emits an opaque
* base64-encoded msgpack object as its cursor — see the cursor-info function
* in `ArtworkService.resolveArtworksFromES`, which only emits the four
* fields below (id/seed/sort/s2).
*
* Why validate the shape here:
*
* 1. `parseGeneralCursorBasedPaginationInput` throws a `GraphQLError` on
*    base64/msgpack failure — in this oRPC route that surfaces as a 500.
*    Validating at the Zod boundary turns it into a clean 400.
* 2. Field-type sanity: `searchArtwork` forwards `sort`/`s2` to ES as
*    `search_after`. A cursor like `{ sort: {} }` would otherwise pass
*    through and trigger an ES error. Validating per-field types keeps
*    that 400 at our boundary.
*
* Cursor-injection (e.g. `{ args: { loraId: ... } }` overriding the
* handler-controlled `args`) is also defended against here via
* `z.strictObject` — but the primary defense is the upstream allow-list
* in `resolveArtworksFromES` (see PR #10761), which `_.pick`s the same
* four fields before assembling the search context. This schema is the
* boundary check; that allow-list is the service-side guarantee.
*/
const decodedEsArtworkCursorSchema = z.strictObject({
	id: z.bigint().optional(),
	seed: z.union([z.string(), z.number()]).nullish(),
	sort: z.array(z.union([
		z.string(),
		z.number(),
		z.null(),
		z.boolean()
	])).optional(),
	s2: z.unknown().optional()
});
const esArtworkCursorSchema = z.string().min(1).max(1024).regex(/^[A-Za-z0-9+/]+=*$/, "cursor must be base64").refine((value) => {
	let decoded;
	try {
		decoded = decodeBase64Msgpack(value);
	} catch {
		return false;
	}
	return decodedEsArtworkCursorSchema.safeParse(decoded).success;
}, { message: "cursor has unexpected shape" });
const styleKeyItemSchema = z.object({
	id: z.string(),
	name: z.string(),
	artworks: z.array(z.object({
		artworkId: z.string(),
		mediaId: z.string(),
		thumbnailUrl: z.string().url().optional()
	})),
	tags: z.array(z.string()),
	sortWeight: z.number().optional()
});
const modelVersionIdSchema = z.coerce.bigint();
const ModelFeatureVisibilityStatus = {
	ON: "on",
	OFF: "off",
	COMING_SOON: "comingSoon"
};
const ModelFeatureVisibilitySource = {
	VERSION: "version",
	MODEL_TYPE: "modelType"
};
const modelFeatureVisibilityStatusSchema = z.enum([
	ModelFeatureVisibilityStatus.ON,
	ModelFeatureVisibilityStatus.OFF,
	ModelFeatureVisibilityStatus.COMING_SOON
]);
const modelFeatureSchema = z.object({
	featureName: z.enum(generationModelCompatibilityKeyLiterals),
	status: modelFeatureVisibilityStatusSchema,
	source: z.enum([ModelFeatureVisibilitySource.VERSION, ModelFeatureVisibilitySource.MODEL_TYPE])
});
const allowedModelTypesSchema = z.enum(allowedModelTypeLiterals);
const InferenceProfileFlag = {
	DEFAULT: "default",
	CUSTOM: "custom",
	MEMBERSHIP_ONLY: "membershipOnly",
	HIDDEN: "hidden"
};
const inferenceProfileFlagSchema = z.enum([
	InferenceProfileFlag.DEFAULT,
	InferenceProfileFlag.CUSTOM,
	InferenceProfileFlag.MEMBERSHIP_ONLY,
	InferenceProfileFlag.HIDDEN
]);
const inferenceProfileSchema = z.object({
	profileName: z.string(),
	samplingSteps: z.number().int().nullable(),
	minSteps: z.number().int().nullable(),
	maxSteps: z.number().int().nullable(),
	profileFlag: inferenceProfileFlagSchema,
	negativePrompt: modelFeatureVisibilityStatusSchema
});
const inferenceProfilesResponseSchema = z.object({ profiles: z.array(inferenceProfileSchema.extend({
	title: z.string(),
	desc: z.string()
})) });
const modelFeaturesResponseSchema = z.object({
	modelType: allowedModelTypesSchema,
	features: z.array(modelFeatureSchema)
});
const modelIdSchema = z.coerce.bigint();
const fileUploadRecordSchema = z.object({
	id: z.string(),
	userId: z.string(),
	externalId: z.string(),
	filename: z.string(),
	fileType: z.string(),
	fileSize: z.number().int(),
	isFinished: z.boolean(),
	extra: z.record(z.string(), z.unknown()).nullable(),
	createdAt: z.iso.datetime(),
	updatedAt: z.iso.datetime(),
	status: z.string().nullable(),
	hash: z.string().nullable(),
	asset: z.string()
});
const imageVariantSchema = z.enum([
	"PUBLIC",
	"THUMBNAIL",
	"STILL_THUMBNAIL"
]);
const imageUrlSchema = z.object({
	variant: imageVariantSchema.nullable(),
	url: z.string().nullable()
});
const importExternalMediaItemSchema = z.object({
	id: z.string(),
	type: z.enum(["IMAGE", "VIDEO"]),
	width: z.number().int().nullable(),
	height: z.number().int().nullable(),
	urls: z.array(imageUrlSchema).nullable(),
	imageType: z.string().nullable(),
	fileUrl: z.string().nullable(),
	duration: z.number().nullable(),
	thumbnailUrl: z.string().nullable(),
	hlsUrl: z.string().nullable(),
	size: z.number().int().nullable()
});
const importExternalModelItemSchema = z.object({
	name: z.string().nullable(),
	url: z.string().nullable(),
	type: z.string().nullable(),
	mediaList: z.array(importExternalMediaItemSchema),
	createdAt: z.iso.datetime().nullable(),
	updatedAt: z.iso.datetime().nullable()
});
const importExternalModelExtraSchema = z.object({ triggerWords: z.array(z.string()).nullable() });
const importedTackSchema = z.object({
	id: z.string(),
	parentId: z.string().nullable().meta({
		deprecated: true,
		description: "Deprecated. Always returns `null`. Will be removed in a follow-up PR."
	}),
	codeName: z.string(),
	category: z.string(),
	defaultName: z.string(),
	mediaId: z.string().nullable(),
	weight: z.number().int(),
	createdAt: z.iso.datetime(),
	updatedAt: z.iso.datetime(),
	tackTerms: z.array(z.object({
		id: z.string(),
		category: z.string(),
		name: z.string()
	})).meta({
		deprecated: true,
		description: "Deprecated. Always returns `[]`; use `displayName` instead. Will be removed in a follow-up PR."
	}),
	thumbnailUrl: z.string().url().nullable(),
	displayName: z.string()
});
const importExternalModelOutputSchema = z.object({
	fileId: z.string().nullable(),
	file: fileUploadRecordSchema.nullable(),
	exists: z.boolean().nullable(),
	hash: z.string().nullable(),
	type: z.string().nullable(),
	name: z.string().nullable(),
	description: z.string().nullable(),
	isNsfw: z.boolean().nullable(),
	author: z.string().nullable(),
	tacks: z.array(importedTackSchema),
	extra: importExternalModelExtraSchema.nullable(),
	images: z.array(z.string()),
	mediaList: z.array(importExternalMediaItemSchema),
	items: z.array(importExternalModelItemSchema)
});
const modelPermissionResponseSchema = z.object({
	modelId: z.string(),
	userId: z.string(),
	permission: z.number()
});
const listArtworksByModelInputSchema = z.object({
	modelId: modelIdSchema,
	after: esArtworkCursorSchema.optional(),
	first: z.coerce.number().int().positive().max(50).default(20)
});
const listArtworksByModelErrors = { NOT_FOUND: {
	status: 404,
	message: "Generation model not found"
} };
const generationModelContract = oc.prefix("/generation-model").tag("GenerationModel").router({
	getMyModelPermission: oc.route({
		summary: "Get current user permissions for a model",
		method: "GET",
		path: "/{modelId}/permission"
	}).input(z.object({ modelId: modelIdSchema })).output(modelPermissionResponseSchema),
	getStyleKeys: oc.route({
		summary: "Get style keys for a model version",
		method: "GET",
		path: "/{modelVersionId}/style-keys"
	}).input(z.object({ modelVersionId: modelVersionIdSchema })).output(z.array(styleKeyItemSchema)).errors({ NOT_FOUND: {
		status: 404,
		message: "No style keys found for this model version"
	} }),
	getFeaturesByModelVersionId: oc.route({
		summary: "Get features for a model",
		method: "GET",
		path: "/{modelVersionId}/features"
	}).input(z.object({ modelVersionId: modelVersionIdSchema })).output(modelFeaturesResponseSchema).errors({
		NOT_FOUND: {
			status: 404,
			message: "No default features found for this model version"
		},
		UNSUPPORTED_MODEL_TYPE: {
			status: 400,
			message: "Model type is not supported for feature visibility"
		}
	}),
	getInferenceProfiles: oc.route({
		summary: "Get inference profiles for a model version",
		method: "GET",
		path: "/{modelVersionId}/inference-profiles"
	}).input(z.object({ modelVersionId: modelVersionIdSchema })).output(inferenceProfilesResponseSchema).errors({ NOT_FOUND: {
		status: 404,
		message: "Model version not found"
	} }),
	getSizeConfig: oc.route({
		summary: "Get size configuration for a model version",
		method: "GET",
		path: "/{modelVersionId}/size-config"
	}).input(z.object({ modelVersionId: modelVersionIdSchema })).output(sizeConfigResponseSchema).errors({ NOT_FOUND: {
		status: 404,
		message: "Model version not found"
	} }),
	getTrendingArtworksByModel: oc.route({
		summary: "Get trending artworks for a generation model",
		description: "Returns trending public artworks generated with the given model, ranked by the `trending1` ES function-score (time-decay + log-scaled likes + quality signals + randomness — the same ranking the web app's \"Trending\" UI option resolves to via the GraphQL `feed: \"trending1\"` path). Distinct from `/most-liked`, which sorts by raw all-time like count.",
		method: "GET",
		path: "/{modelId}/artworks/trending"
	}).input(listArtworksByModelInputSchema).output(getTrendingArtworksOutputSchema).errors(listArtworksByModelErrors),
	getLatestArtworksByModel: oc.route({
		summary: "Get latest artworks for a generation model",
		description: "Returns the newest public artworks generated with the given model, ordered by `createdAt` descending. Same view-permission gate, type-default mirroring, and output shape as `/trending`; only the orderBy differs.",
		method: "GET",
		path: "/{modelId}/artworks/latest"
	}).input(listArtworksByModelInputSchema).output(getTrendingArtworksOutputSchema).errors(listArtworksByModelErrors),
	getMostLikedArtworksByModel: oc.route({
		summary: "Get most-liked artworks for a generation model",
		description: "Returns public artworks generated with the given model, ordered by raw all-time `likedCount` descending. Mirrors the GraphQL `artworks(loraId, orderBy: \"-markInfo.likedCount\")` query the web app uses for the \"Most Liked\" UI option. Distinct from `/trending`, which uses the `trending1` function-score ranking.",
		method: "GET",
		path: "/{modelId}/artworks/most-liked"
	}).input(listArtworksByModelInputSchema).output(getTrendingArtworksOutputSchema).errors(listArtworksByModelErrors),
	importExternalModel: oc.route({
		summary: "Import an external model by URL",
		method: "POST",
		path: "/import-external"
	}).input(z.object({
		url: z.string().url(),
		token: z.string().optional()
	})).output(importExternalModelOutputSchema).errors({
		BAD_REQUEST: {
			status: 400,
			message: "Bad request"
		},
		FORBIDDEN: {
			status: 403,
			message: "Forbidden"
		},
		MODEL_IS_NOT_PUBLIC: {
			status: 403,
			message: "This model is not public"
		}
	})
});
//#endregion
export { InferenceProfileFlag, ModelFeatureVisibilitySource, ModelFeatureVisibilityStatus, allowedModelTypesSchema, esArtworkCursorSchema, fileUploadRecordSchema, generationModelContract, inferenceProfileFlagSchema, inferenceProfileSchema, modelFeatureVisibilityStatusSchema };
